import { createFileRoute } from "@tanstack/react-router";
import { pageMeta } from "@/lib/seo";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-shim";
import { useState } from "react";
import { getMyTrip } from "@/lib/trip.functions";
import {
  exportMontageData,
  recordMontageExport,
  listMontageExports,
} from "@/lib/admin.functions";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Sparkles, FileJson, FileSpreadsheet, ListOrdered } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/admin/PageHeader";
import { RoavaCard, StatusChip, PrimaryButton } from "@/components/roava";

export const Route = createFileRoute("/_authenticated/admin/export")({
  head: () => pageMeta({ title: 'AI Montage Export — Roava by WGP Apps', description: 'Export AI-generated trip montages and highlight reels from your shared trip.', path: '/admin/export' }),
  component: ExportPage,
});

type ExportType = "json_manifest" | "csv_metadata" | "media_download_list";

function ExportPage() {
  const tripFn = useServerFn(getMyTrip);
  const exportFn = useServerFn(exportMontageData);
  const recordFn = useServerFn(recordMontageExport);
  const listFn = useServerFn(listMontageExports);
  const qc = useQueryClient();

  const { data: tripData } = useQuery({ queryKey: ["my-trip"], queryFn: () => tripFn({}) });
  const tripId = tripData?.trip?.id;

  const { data: recent } = useQuery({
    queryKey: ["montage-exports", tripId],
    queryFn: () => listFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
  });

  const [scope, setScope] = useState<"all" | "favorites_only">("all");
  const [excludeMarked, setExcludeMarked] = useState(true);
  const [includeLocation, setIncludeLocation] = useState(true);
  const [includeUserNames, setIncludeUserNames] = useState(true);
  const [includeExif, setIncludeExif] = useState(true);
  const [includeAITags, setIncludeAITags] = useState(true);
  const [busy, setBusy] = useState<ExportType | null>(null);

  async function run(kind: ExportType) {
    if (!tripId) return;
    setBusy(kind);
    try {
      const manifest = await exportFn({
        data: {
          tripId,
          scope,
          excludeMarked,
          includeLocation,
          includeUserNames,
          includeExif,
          includeAITags,
        },
      });
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      if (kind === "json_manifest") {
        download(
          new Blob([JSON.stringify(manifest, null, 2)], { type: "application/json" }),
          `montage-manifest-${stamp}.json`,
        );
      } else if (kind === "csv_metadata") {
        download(
          new Blob([toCsv(manifest.media_assets as any[])], { type: "text/csv" }),
          `montage-metadata-${stamp}.csv`,
        );
      } else {
        const lines = (manifest.media_assets as any[])
          .filter((m) => m.signed_url)
          .map((m) => `${m.signed_url}\t${m.original_filename ?? m.id}`);
        download(
          new Blob([lines.join("\n")], { type: "text/plain" }),
          `montage-downloads-${stamp}.txt`,
        );
      }
      await recordFn({ data: { tripId, exportType: kind } });
      qc.invalidateQueries({ queryKey: ["montage-exports", tripId] });
      toast.success("Export generated");
    } catch (e: any) {
      toast.error(e.message ?? "Export failed");
    } finally {
      setBusy(null);
    }
  }

  return (
    <main className="mx-auto max-w-3xl px-5 md:px-8 py-6 md:py-8 grid gap-5">
      <PageHeader
        title="AI Montage Export"
        subtitle="Bundle trip media + metadata for an external AI pipeline to assemble a montage."
      />


      <RoavaCard title="Options">
        <div className="grid gap-5">
          <div className="grid gap-2">
            <Label className="text-xs uppercase text-[var(--roava-text-muted)]">Scope</Label>
            <RadioGroup value={scope} onValueChange={(v) => setScope(v as any)} className="flex gap-6">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <RadioGroupItem value="all" /> All media
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <RadioGroupItem value="favorites_only" /> Favorites only
              </label>
            </RadioGroup>
          </div>

          <ToggleRow label="Exclude items marked “exclude from montage”" checked={excludeMarked} onChange={setExcludeMarked} />
          <ToggleRow label="Include location trail" checked={includeLocation} onChange={setIncludeLocation} />
          <ToggleRow label="Include user names" checked={includeUserNames} onChange={setIncludeUserNames} />
          <ToggleRow label="Include EXIF metadata" checked={includeExif} onChange={setIncludeExif} />
          <ToggleRow label="Include AI tags & quality scores" checked={includeAITags} onChange={setIncludeAITags} />
        </div>
      </RoavaCard>

      <RoavaCard title="Export formats">
        <div className="grid gap-4">
          <div className="grid gap-3 md:grid-cols-3">
            <FormatBtn Icon={FileJson} label="JSON manifest" sub="Full structured payload" busy={busy === "json_manifest"} onClick={() => run("json_manifest")} />
            <FormatBtn Icon={FileSpreadsheet} label="CSV metadata" sub="Flat row per media asset" busy={busy === "csv_metadata"} onClick={() => run("csv_metadata")} />
            <FormatBtn Icon={ListOrdered} label="Media download list" sub="Signed URLs (24h)" busy={busy === "media_download_list"} onClick={() => run("media_download_list")} />
          </div>

          <PrimaryButton
            size="lg"
            className="mt-2 w-full"
            disabled={!tripId || busy !== null}
            onClick={() => run("json_manifest")}
          >
            <Sparkles className="size-4 mr-2" />
            {busy ? "Generating…" : "Generate Montage Export"}
          </PrimaryButton>
        </div>
      </RoavaCard>

      <RoavaCard title="Recent exports">
        {!recent?.rows.length && <p className="text-sm text-[var(--roava-text-muted)]">No exports yet.</p>}
        <ul className="divide-y divide-[var(--roava-border)]">
          {recent?.rows.map((r: any) => (
            <li key={r.id} className="py-2 flex items-center justify-between text-sm">
              <span className="text-[var(--roava-text-muted)]">{new Date(r.created_at).toLocaleString()}</span>
              <StatusChip variant="neutral" className="font-mono">{r.export_type}</StatusChip>
            </li>
          ))}
        </ul>
      </RoavaCard>
    </main>
  );
}

function ToggleRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center justify-between gap-4 cursor-pointer">
      <span className="text-sm">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}

function FormatBtn({ Icon, label, sub, busy, onClick }: any) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      className="text-left rounded-[16px] border border-[var(--roava-border)] bg-[var(--roava-card)] p-4 hover:bg-accent transition disabled:opacity-60"
    >
      <Icon className="size-5 mb-2 text-[var(--roava-blue)]" />
      <p className="font-medium text-sm text-[var(--roava-text)]">{label}</p>
      <p className="text-xs text-[var(--roava-text-muted)] mt-0.5">{sub}</p>
      {busy && <p className="text-xs text-[var(--roava-blue)] mt-1">Working…</p>}
    </button>
  );
}

function download(blob: Blob, filename: string) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

function toCsv(rows: any[]): string {
  const cols = [
    "id", "captured_at", "uploaded_at", "uploader_name", "user_id", "media_type",
    "original_filename", "storage_path", "latitude", "longitude", "altitude_meters",
    "heading_degrees", "duration_seconds", "width", "height", "file_size_bytes",
    "camera_make", "camera_model", "ai_tags", "ai_quality_score",
    "is_favorite", "excluded_from_montage", "needs_review", "signed_url",
  ];
  const esc = (v: any) => {
    if (v === null || v === undefined) return "";
    const s = Array.isArray(v) ? v.join("|") : typeof v === "object" ? JSON.stringify(v) : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
}
